import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, UploadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supplierApi } from "@/lib/supplierPortalApi";
import { useSupplierAuth } from "@/contexts/SupplierAuthContext";
import { SupplierPortalLayout } from "./SupplierPortalLayout";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  "CAP Revision Required": "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  Closed: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  "CAP Submitted": "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
  "No CAP Required": "bg-slate-50 text-slate-600 border-slate-200",
  "Pending CAP Submission": "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
};

function StatusBadge({ text }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-semibold border ${STATUS_BADGE[text] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
      {text}
    </span>
  );
}

async function openBlob(url) {
  const { data, headers } = await supplierApi.get(url, { responseType: "blob" });
  const blob = new Blob([data], { type: headers["content-type"] });
  window.open(URL.createObjectURL(blob), "_blank");
}

export default function QNotificationsPage() {
  const { account } = useSupplierAuth();
  const { vendorCode } = useParams();
  const isImpersonating = !!(account?.testing_mode && vendorCode && vendorCode !== account?.vendor_code);

  const [qns, setQns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ cap_root_cause: "", cap_containment: "", cap_corrective: "", cap_preventive: "", cap_responsible: "", cap_target_date: "" });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = isImpersonating ? { as_vendor: vendorCode } : {};
      const { data } = await supplierApi.get("/qms/qnotifications", { params });
      setQns(data.qnotifications || []);
    } catch (err) {
      setLoadError(err.response?.data?.detail || "Could not load Q-Notifications from QMS.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCode]);

  const openDetail = async (qnId) => {
    try {
      const params = isImpersonating ? { as_vendor: vendorCode } : {};
      const { data } = await supplierApi.get(`/qms/qnotifications/${qnId}`, { params });
      setDetail(data);
      if (data.cap_submitted_at || (data.status || "").toLowerCase() === "closed") {
        setForm({
          cap_root_cause: data.cap_root_cause || "", cap_containment: data.cap_containment || "",
          cap_corrective: data.cap_corrective || "", cap_preventive: data.cap_preventive || "",
          cap_responsible: data.cap_responsible || "", cap_target_date: data.cap_target_date || "",
        });
      } else {
        setForm({
          cap_root_cause: "", cap_containment: "", cap_corrective: "", cap_preventive: "",
          cap_responsible: "", cap_target_date: "",
        });
      }
      setEvidenceFile(null);
      setSubmitError("");
    } catch (err) {
      toast.error("Could not open this Q-Notification", { description: err.response?.data?.detail });
    }
  };

  const submitCap = async () => {
    const { cap_root_cause, cap_containment, cap_corrective, cap_preventive, cap_responsible, cap_target_date } = form;
    if (!(cap_root_cause.trim() && cap_containment.trim() && cap_corrective.trim() && cap_preventive.trim() && cap_responsible.trim() && cap_target_date)) {
      setSubmitError("All CAP fields are required.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await supplierApi.post(`/qms/qnotifications/${detail.id || detail.qn_id}/cap`, form);
      if (evidenceFile) {
        const fd = new FormData();
        fd.append("file", evidenceFile);
        await supplierApi.post(`/qms/qnotifications/${detail.id || detail.qn_id}/attachments`, fd);
      }
      toast.success("Corrective Action Plan submitted to QMS");
      setDetail(null);
      load();
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Could not submit this CAP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const viewInQms = () => {
    window.location.href = `${BACKEND_URL}/api/supplier-portal/qms/sso-redirect?next=${encodeURIComponent(`/vendor/qnotifications/${detail.id || detail.qn_id}`)}`;
  };

  return (
    <SupplierPortalLayout active="qnotifications" vendorCode={vendorCode} isImpersonating={isImpersonating} pageTitle="Q-Notifications" pageSubtitle="Live from QMS">
      {loadError && <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-sm rounded-lg px-4 py-3 mb-4" data-testid="qn-load-error">{loadError}</div>}
      <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-x-auto" data-testid="qn-table">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-[#F1F5F9] text-[#334155] text-xs font-bold font-heading uppercase tracking-wide">
            <tr>
              <th className="border border-[#E2E8F0] p-2 text-left">QN #</th>
              <th className="border border-[#E2E8F0] p-2 text-left">Part</th>
              <th className="border border-[#E2E8F0] p-2 text-left">Defect</th>
              <th className="border border-[#E2E8F0] p-2 text-right">Qty</th>
              <th className="border border-[#E2E8F0] p-2 text-left">Status</th>
              <th className="border border-[#E2E8F0] p-2 text-left">CAP Due</th>
              <th className="border border-[#E2E8F0] p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && qns.length === 0 && (
              <tr><td colSpan={7} className="border border-[#E2E8F0] px-2 py-6 text-center text-slate-500">No Q-Notifications found.</td></tr>
            )}
            {qns.map((q) => {
              const id = q.id || q.qn_id;
              const displayId = q.display_id || q.nc_no || id;
              const part = q.part_no || q.part_name || q.part || q.material_code;
              const defect = q.problem_desc || q.defect || q.defect_type;
              const qty = q.total_qty ?? q.inspection_qty ?? q.affected_qty ?? q.qty;
              return (
                <tr key={id} className="bg-white odd:bg-[#F9FAFB] hover:bg-[#F0F4F8] transition-colors duration-150" data-testid={`qn-row-${id}`}>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 font-data font-semibold text-[#1E40AF]">{displayId}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 font-data">{part}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5">{defect}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 text-right font-data">{qty}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5"><StatusBadge text={q.status_label} /></td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 font-data text-xs">{q.cap_target_date || q.cap_due || "-"}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 text-right">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => openDetail(id)} data-testid={`qn-view-button-${id}`}>View</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-lg max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qn-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              Q-Notification {detail?.display_id || detail?.nc_no || detail?.id || detail?.qn_id}
              <StatusBadge text={detail?.status_label} />
            </DialogTitle>
            <DialogDescription>
              {detail?.part_no || detail?.part_name || detail?.part || detail?.material_code} — {detail?.problem_desc || detail?.defect || detail?.defect_type}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="rounded-md text-xs" onClick={viewInQms} data-testid="qn-view-in-qms-button">
              <ArrowSquareOut size={14} className="mr-1.5" /> View full details in QMS
            </Button>
          </div>

          {Array.isArray(detail?.photos) && detail.photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {detail.photos.map((p, i) => (
                <Button key={i} size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => openBlob(`/qms/qnotifications/${detail.id || detail.qn_id}/photo?path=${encodeURIComponent(p)}`)} data-testid={`qn-photo-button-${i}`}>
                  Photo {i + 1}
                </Button>
              ))}
            </div>
          )}

          {(() => {
            const isReadOnly = !!detail?.cap_submitted_at || (detail?.status || "").toLowerCase() === "closed";
            if (isReadOnly) {
              return (
                <div className="space-y-3" data-testid="qn-cap-readonly-view">
                  <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-sm text-[#065F46] flex items-center gap-2">
                    <CheckCircle size={16} weight="fill" />
                    {detail.cap_submitted_at ? `CAP submitted on ${new Date(detail.cap_submitted_at).toLocaleDateString()}.` : "This Q-Notification is closed."}
                  </div>
                  {form.cap_root_cause && (
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div><span className="font-semibold text-slate-600">Root Cause: </span>{form.cap_root_cause}</div>
                      <div><span className="font-semibold text-slate-600">Containment: </span>{form.cap_containment}</div>
                      <div><span className="font-semibold text-slate-600">Corrective Action: </span>{form.cap_corrective}</div>
                      <div><span className="font-semibold text-slate-600">Preventive Action: </span>{form.cap_preventive}</div>
                      <div><span className="font-semibold text-slate-600">Responsible: </span>{form.cap_responsible} <span className="font-semibold text-slate-600 ml-3">Target Date: </span>{form.cap_target_date}</div>
                    </div>
                  )}
                </div>
              );
            }
            if (detail?.status_label === "No CAP Required") {
              return <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">No CAP is required for this Q-Notification.</div>;
            }
            return (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Root Cause</label>
                <Textarea value={form.cap_root_cause} onChange={(e) => setForm((f) => ({ ...f, cap_root_cause: e.target.value }))} className="mt-1 rounded-md" data-testid="qn-cap-root-cause-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Containment</label>
                <Textarea value={form.cap_containment} onChange={(e) => setForm((f) => ({ ...f, cap_containment: e.target.value }))} className="mt-1 rounded-md" data-testid="qn-cap-containment-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Corrective Action</label>
                <Textarea value={form.cap_corrective} onChange={(e) => setForm((f) => ({ ...f, cap_corrective: e.target.value }))} className="mt-1 rounded-md" data-testid="qn-cap-corrective-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Preventive Action</label>
                <Textarea value={form.cap_preventive} onChange={(e) => setForm((f) => ({ ...f, cap_preventive: e.target.value }))} className="mt-1 rounded-md" data-testid="qn-cap-preventive-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Responsible Person</label>
                  <Input value={form.cap_responsible} onChange={(e) => setForm((f) => ({ ...f, cap_responsible: e.target.value }))} className="mt-1 rounded-md" data-testid="qn-cap-responsible-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Target Date</label>
                  <Input type="date" value={form.cap_target_date} onChange={(e) => setForm((f) => ({ ...f, cap_target_date: e.target.value }))} className="mt-1 rounded-md" data-testid="qn-cap-target-date-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Evidence (optional)</label>
                <label className="mt-1 flex items-center gap-1.5 text-xs border border-[#E2E8F0] rounded-md px-2.5 h-9 cursor-pointer text-slate-600 hover:bg-slate-50 w-fit">
                  <UploadSimple size={14} />
                  <span className="truncate">{evidenceFile ? evidenceFile.name : "Attach file"}</span>
                  <input type="file" className="hidden" onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)} data-testid="qn-cap-evidence-input" />
                </label>
              </div>
              {submitError && <div className="text-sm text-[#991B1B]" data-testid="qn-cap-submit-error">{submitError}</div>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-md" onClick={() => setDetail(null)}>Cancel</Button>
                <Button onClick={submitCap} disabled={submitting} className="rounded-md bg-[#1E40AF] hover:bg-[#1E3A8A]" data-testid="qn-cap-submit-button">
                  {submitting ? "Submitting..." : "Submit CAP"}
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </SupplierPortalLayout>
  );
}
